import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTimePicker } from '@/components/DateTimePicker';
import { httpGet } from '@/api/httpClient';
import { computeChartRange, sanitizeChartValue } from '@/utils/chartRanges';

type TimeseriesResponse = {
  attribute_id: string;
  measurement: string;
  start: string;
  stop: string;
  field?: string;
  query?: string;
  points: Array<{ t: string; v: unknown }>;
};

type AttributeTimeseriesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  attribute: { uuid: string; name: string; roomName?: string; unit?: string | null; attributeTypeCode?: string | null } | null;
};

export function AttributeTimeseriesModal({ isOpen, onClose, attribute }: AttributeTimeseriesModalProps) {
  const [start, setStart] = useState<string>('');
  const [stop, setStop] = useState<string>('');
  const [limit, setLimit] = useState<number>(2000);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [isQueryOpen, setIsQueryOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen || !attribute?.uuid) return;

    const now = new Date();
    const from = new Date(now.getTime() - 48 * 60 * 60 * 1000);
    const formatLocal = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    setStart(formatLocal(from));
    setStop(formatLocal(now));
    setLimit(2000);
    setHoverIdx(null);
    setIsQueryOpen(false);
  }, [isOpen, attribute?.uuid]);

  const startISO = start ? new Date(start).toISOString() : null;
  const stopISO = stop ? new Date(stop).toISOString() : null;
  const dateError = start && stop && new Date(stop) <= new Date(start) ? 'La date de début doit être antérieure à la date de fin' : null;

  const timeseriesQuery = useQuery({
    queryKey: ['influxV1TimeseriesModal', attribute?.uuid, startISO, stopISO, limit],
    enabled: isOpen && !!attribute?.uuid && !!startISO && !!stopISO && !dateError,
    queryFn: () =>
      httpGet<TimeseriesResponse>('/influx/v1/timeseries', {
        attribute_uuid: attribute?.uuid ?? undefined,
        start: startISO ?? undefined,
        stop: stopISO ?? undefined,
        limit: String(limit),
      }),
    staleTime: 0,
  });

  const series = useMemo(() => {
    const raw = timeseriesQuery.data?.points ?? [];
    const field = timeseriesQuery.data?.field ?? null;

    // Prefer explicit field to decide the rendering mode.
    const preferString = field === 'value_s';

    // Build normalized points (keep time, and either numeric v or string s).
    const numericPts: Array<{ t: number; v: number }> = [];
    const stringPts: Array<{ t: number; s: string }> = [];

    for (const p of raw) {
      const t = Date.parse(p.t);
      if (!Number.isFinite(t)) continue;

      const vv = (p as any).v;

      if (!preferString) {
        const n = typeof vv === 'number' ? vv : typeof vv === 'string' ? Number(vv) : NaN;
        if (Number.isFinite(n)) {
          numericPts.push({ t, v: n });
          continue;
        }
      }

      // String fallback (also used for value_s)
      const s = vv === null || vv === undefined ? '' : String(vv);
      if (s.trim() === '') continue;
      stringPts.push({ t, s });
    }

    if (preferString || (numericPts.length === 0 && stringPts.length > 0)) {
      // Categories in order of appearance
      const unique: string[] = [];
      const seen = new Set<string>();
      for (const p of stringPts) {
        if (seen.has(p.s)) continue;
        seen.add(p.s);
        unique.push(p.s);
      }

      // Special ordering for MODE: OFF at bottom, HEAT at top.
      const atc = (attribute?.attributeTypeCode ?? '').toUpperCase();
      let categories = unique.slice();
      if (atc === 'MODE') {
        const rank = (s: string): number => {
          const v = s.trim().toUpperCase();
          if (v === 'OFF') return 0;
          if (v === 'COOL') return 1;
          if (v === 'HEAT') return 2;
          return 1.5;
        };
        categories.sort((a, b) => {
          const ra = rank(a);
          const rb = rank(b);
          if (ra !== rb) return ra - rb;
          return a.localeCompare(b);
        });
      }

      const idxByValue = new Map<string, number>();
      categories.forEach((c, i) => idxByValue.set(c, i));

      const catPts = stringPts.map((p) => ({ t: p.t, v: idxByValue.get(p.s) ?? 0, s: p.s }));
      return { mode: 'categorical' as const, points: catPts, categories, field };
    }

    return { mode: 'numeric' as const, points: numericPts, categories: [] as string[], field };
  }, [timeseriesQuery.data]);

  if (!isOpen) return null;

  const title = attribute?.roomName ? `${attribute.roomName} — ${attribute.name}` : attribute?.name ?? 'Attribute';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        aria-label="Close modal backdrop"
        onClick={onClose}
      />

      <div className="relative w-full max-w-5xl max-h-[85vh] overflow-hidden bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl shadow-xl">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-[#1E293B] flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">{title}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              UUID: <span className="font-mono">{attribute?.uuid?.substring(0, 8)}…</span>
              {attribute?.unit ? <span className="ml-2">unit: {attribute.unit}</span> : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-sm bg-slate-100 hover:bg-slate-200 dark:bg-[#0B1116] dark:hover:bg-slate-800 transition-colors"
          >
            Fermer
          </button>
        </div>

        <div className="p-5 overflow-y-auto max-h-[calc(85vh-64px)]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <DateTimePicker value={start} onChange={setStart} label="Début" />
            </div>
            <div>
              <DateTimePicker value={stop} onChange={setStop} label="Fin" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1 dark:text-gray-300">Limit</label>
              <input
                type="number"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                min="1"
                max="50000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-[#1E293B] dark:border-gray-600 dark:text-white"
              />
            </div>
          </div>

          {dateError && <div className="mb-3 text-sm text-rose-600 dark:text-rose-400">{dateError}</div>}
          {timeseriesQuery.isLoading && <div className="text-sm text-slate-600 dark:text-slate-300">Chargement des points…</div>}
          {timeseriesQuery.isError && (
            <div className="text-sm text-rose-600 dark:text-rose-400">Erreur lors du chargement des points.</div>
          )}

          {timeseriesQuery.data && (
            <div className="relative bg-slate-50 dark:bg-[#1E293B] border border-slate-200 dark:border-gray-700 rounded-lg p-3 overflow-x-auto">
              {series.points.length ? (
                (() => {
                  const pts: any[] = series.points;
                  const sanitizedPts =
                    series.mode === 'categorical'
                      ? pts
                      : pts.map((p) => ({
                          ...p,
                          v: sanitizeChartValue(
                            p.v,
                            attribute?.name ?? '',
                            attribute?.unit ?? null,
                            attribute?.attributeTypeCode ?? null
                          ),
                        }));
                  const values = sanitizedPts.map((p) => p.v);
                  const chartRange =
                    series.mode === 'categorical'
                      ? null
                      : computeChartRange(values, attribute?.name ?? '', attribute?.unit ?? null, attribute?.attributeTypeCode ?? null);
                  const minV = chartRange ? chartRange.min : (values.length ? Math.min(...values) : 0);
                  const maxV = chartRange ? chartRange.max : (values.length ? Math.max(...values) : 0);
                  const range = chartRange ? chartRange.range : maxV - minV || 1;
                  const minT = Math.min(...sanitizedPts.map((p) => p.t));
                  const maxT = Math.max(...sanitizedPts.map((p) => p.t));
                  const w = 900;
                  const h = 260;
                  const pad = 30;
                  const leftPad = 60;
                  const innerW = w - leftPad - pad;
                  const innerH = h - 2 * pad;
                  const getX = (i: number) => leftPad + (i / (sanitizedPts.length - 1 || 1)) * innerW;
                  const getY = (v: number) => {
                    const clamped = Math.max(minV, Math.min(maxV, v));
                    return h - pad - ((clamped - minV) / range) * innerH;
                  };
                  const d = sanitizedPts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(p.v)}`).join(' ');

                  const yTicks = series.mode === 'categorical' ? Math.min(6, Math.max(1, series.categories.length - 1)) : 4;
                  const xTicks = 4;
                  const formatY = (v: number) => {
                    if (!Number.isFinite(v)) return '';
                    if (series.mode === 'categorical') {
                      const idx = Math.max(0, Math.min(series.categories.length - 1, Math.round(v)));
                      const label = series.categories[idx] ?? '';
                      // avoid super long labels
                      return label.length > 18 ? `${label.slice(0, 18)}…` : label;
                    }
                    return (Math.round(v * 10) / 10).toString();
                  };
                  const formatX = (ms: number) =>
                    new Date(ms).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

                  const hoverI = hoverIdx !== null ? Math.max(0, Math.min(sanitizedPts.length - 1, hoverIdx)) : null;
                  const hoverPt = hoverI !== null ? sanitizedPts[hoverI] : null;
                  const hoverX = hoverI !== null ? getX(hoverI) : null;
                  const hoverY = hoverPt ? getY(hoverPt.v) : null;
                  const hoverLabelX = hoverPt ? formatX(hoverPt.t) : '';
                  const hoverLabelY =
                    hoverPt && Number.isFinite(hoverPt.v)
                      ? series.mode === 'categorical'
                        ? (hoverPt.s ?? '')
                        : (() => {
                            const v = hoverPt.v;
                            const outOfRange = v < minV || v > maxV;
                            return (Math.round(v * 100) / 100).toString() + (outOfRange ? ' (hors plage)' : '');
                          })()
                      : '';

                  return (
                    <svg
                      viewBox={`0 0 ${w} ${h}`}
                      className="w-full h-auto min-w-[900px]"
                      onMouseLeave={() => setHoverIdx(null)}
                      onMouseMove={(e) => {
                        const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
                        if (rect.width <= 0) return;
                        const x = ((e.clientX - rect.left) / rect.width) * w;
                        if (x < leftPad || x > w - pad) {
                          setHoverIdx(null);
                          return;
                        }
                        const ratio = (x - leftPad) / innerW;
                        const idx = Math.round(ratio * (sanitizedPts.length - 1));
                        setHoverIdx(Math.max(0, Math.min(sanitizedPts.length - 1, idx)));
                      }}
                    >
                      {Array.from({ length: yTicks + 1 }).map((_, i) => {
                        const vv = minV + (i / yTicks) * range;
                        const y = getY(vv);
                        return (
                          <g key={`y-${i}`}>
                            <line x1={leftPad} x2={w - pad} y1={y} y2={y} stroke="rgba(148,163,184,0.25)" strokeWidth="1" />
                            <text x={leftPad - 8} y={y + 4} fontSize="10" fill="rgba(148,163,184,0.9)" textAnchor="end">
                              {formatY(vv)}
                            </text>
                          </g>
                        );
                      })}

                      {Array.from({ length: xTicks + 1 }).map((_, i) => {
                        const x = leftPad + (i / xTicks) * innerW;
                        const ms = minT + (i / xTicks) * (maxT - minT || 1);
                        return (
                          <g key={`x-${i}`}>
                            <line x1={x} x2={x} y1={pad} y2={h - pad} stroke="rgba(148,163,184,0.18)" strokeWidth="1" />
                            <text x={x} y={h - 10} fontSize="10" fill="rgba(148,163,184,0.9)" textAnchor="middle">
                              {formatX(ms)}
                            </text>
                          </g>
                        );
                      })}

                      <line x1={leftPad} x2={leftPad} y1={pad} y2={h - pad} stroke="rgba(148,163,184,0.7)" strokeWidth="1.2" />
                      <line x1={leftPad} x2={w - pad} y1={h - pad} y2={h - pad} stroke="rgba(148,163,184,0.7)" strokeWidth="1.2" />

                      <text x={(leftPad + (w - pad)) / 2} y={h - 2} fontSize="11" fill="rgba(148,163,184,0.9)" textAnchor="middle">
                        Temps
                      </text>
                      <text
                        x={14}
                        y={(pad + (h - pad)) / 2}
                        fontSize="11"
                        fill="rgba(148,163,184,0.9)"
                        textAnchor="middle"
                        transform={`rotate(-90 14 ${(pad + (h - pad)) / 2})`}
                      >
                        Valeur
                      </text>

                      <path d={d} fill="none" stroke="rgb(34, 211, 238)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />

                      {hoverPt && hoverX !== null && hoverY !== null && (
                        <g>
                          <line x1={hoverX} x2={hoverX} y1={pad} y2={h - pad} stroke="rgba(34,211,238,0.35)" strokeWidth="1" />
                          <circle cx={hoverX} cy={hoverY} r={4} fill="rgb(34,211,238)" stroke="rgba(15,23,42,0.9)" strokeWidth="2" />

                          {(() => {
                            const boxW = 210;
                            const boxH = 44;
                            const margin = 10;
                            const bx = Math.min(Math.max(leftPad, hoverX - boxW / 2), w - pad - boxW);
                            const by = Math.max(pad, hoverY - boxH - margin);
                            return (
                              <g>
                                <rect
                                  x={bx}
                                  y={by}
                                  width={boxW}
                                  height={boxH}
                                  rx={8}
                                  fill="rgba(15, 23, 42, 0.92)"
                                  stroke="rgba(148,163,184,0.25)"
                                />
                                <text x={bx + 10} y={by + 17} fontSize="11" fill="rgba(226,232,240,0.95)">
                                  {hoverLabelX}
                                </text>
                                <text x={bx + 10} y={by + 34} fontSize="11" fill="rgba(226,232,240,0.95)">
                                  v = {hoverLabelY}
                                </text>
                              </g>
                            );
                          })()}
                        </g>
                      )}
                    </svg>
                  );
                })()
              ) : (
                <div className="text-sm text-slate-500 dark:text-slate-400">Aucun point pour la période.</div>
              )}

              {timeseriesQuery.data?.query && (
                <button
                  type="button"
                  onClick={() => setIsQueryOpen((v) => !v)}
                  className="absolute bottom-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/90 hover:bg-white text-slate-700 shadow border border-slate-200 dark:bg-[#0B1116]/90 dark:hover:bg-[#0B1116] dark:text-slate-200 dark:border-[#1E293B] transition-colors"
                  title="Afficher la requête InfluxDB"
                  aria-label="Afficher la requête InfluxDB"
                >
                  <span className="material-icons-outlined text-[18px]">code</span>
                </button>
              )}
            </div>
          )}

          {isQueryOpen && timeseriesQuery.data?.query && (
            <div className="mt-3">
              <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">Requête InfluxDB (Flux)</div>
              <pre className="text-xs font-mono whitespace-pre-wrap break-words bg-slate-50 dark:bg-[#0B1116] border border-slate-200 dark:border-[#1E293B] rounded-lg p-3 overflow-x-auto">
                {timeseriesQuery.data.query}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

